import type { GroupResponse, GroupType } from '@lms/api-client/backend';
import { Input } from '@lms/ui/input';
import { Label } from '@lms/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@lms/ui/select';
import { cn } from '@lms/ui/utils';
import { type FormEvent, useState } from 'react';
import { useTranslations } from 'use-intl';
import { DropZone } from '@/components/dropzone';
import { SubmitButton } from '@/components/submit-button';
import type { Site } from '@/features/branding/brand-provider';
import {
  type SaveBasicDataResult,
  saveBasicData,
} from '@/features/groups/groups-api';

export default function BasicDataForm({
  className,
  group,
  site,
  onSaved,
}: {
  className?: string;
  group: GroupResponse;
  site: Site;
  onSaved?: () => void;
}) {
  const t = useTranslations();
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [name, setName] = useState(group.name);
  const [unitId, setUnitId] = useState(group.unitId ?? '');
  const [groupType, setGroupType] = useState<GroupType>(group.groupType);
  const [pending, setPending] = useState(false);
  const [formState, setFormState] = useState<SaveBasicDataResult>({
    error: '',
    success: false,
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPending(true);
    try {
      const result = await saveBasicData({
        group: group.id,
        image: imageUrl,
        name,
        unitId,
        groupType,
      });
      setFormState(result);
      if (result.success) {
        onSaved?.();
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      {site.features?.grouplogo && (
        <DropZone
          folder="group-image"
          currentImage={group.logo ?? undefined}
          onUploaded={(url) => setImageUrl(url)}
          onUploadingChange={(u) => setUploading(u)}
          tag={`group-${group.id}`}
        />
      )}
      <form
        onSubmit={handleSubmit}
        className={cn('flex flex-col gap-4 pt-4', className)}
      >
        <div className="flex flex-col gap-2">
          <Label>{t('admin.name')}</Label>
          <Input
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label>{t('admin.unit_id')}</Label>
          <Input
            name="unitId"
            value={unitId}
            onChange={(e) => setUnitId(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label>{t('admin.group_type')}</Label>
          <Select
            name="grouptype"
            value={groupType}
            onValueChange={(v: string) => setGroupType(v as GroupType)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tenant">
                {t('group_alias.tenants_singular')}
              </SelectItem>
              <SelectItem value="supplier">
                {t('group_alias.suppliers_singular')}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        {formState.error && (
          <div className="text-red-500">{formState.error}</div>
        )}
        <SubmitButton
          label={formState.success ? 'Sparat!' : 'Spara'}
          pending={pending}
          disabled={formState.success || uploading}
        />
      </form>
    </>
  );
}
